class TilesCalculator extends HTMLElement {

  constructor() {
    super();

    // raw dataset values are strings → parse once
    this.dimensions = this.dataset.dimensions;
    this.perBoxQty = parseFloat(this.dataset.perBoxQty) || 1;
    this.boxOnly = this.dataset.boxOnly === 'true';

    this.widthCm = parseFloat(this.dataset.widthCm) || 0;
    this.heightCm = parseFloat(this.dataset.heightCm) || 0;

    this.perTilePrice = parseFloat(this.dataset.perTilePrice) || 0;
    this.perTileCompare = parseFloat(this.dataset.perTileCompare) || 0;
    this.pricePerSqm = parseFloat(this.dataset.pricePerSqm) || 0;
    this.perTileUnit = parseFloat(this.dataset.perTileSqm) || 0;

    // selling mode: 'sqm' (area) or 'linear' (linear metre, e.g. borders).
    // perTileUnit is the amount one tile covers in the active unit; unitLabel drives copy.
    this.mode = this.dataset.mode || 'sqm';
    this.perTileUnit = parseFloat(this.dataset.perTileUnit) || this.perTileUnit;
    this.unitLabel = this.dataset.unitLabel || 'SQM';

    this.wastage = parseFloat(this.dataset.wastage) || 0;

    this.sqmInput = this.querySelector('[data-sqm-input]');
    this.tilesInput = this.querySelector('[data-tiles-input]');
    this.boxInput = this.querySelector('[data-box-input]');

    this.checkbox = this.querySelector('.switch [type="checkbox"]');

    this.totalPriceRow = this.querySelector('[data-total-price-row]');
    this.totalPrice = this.querySelector('[data-total-price]');
    this.unitPrice = this.querySelector('[data-unit-price]');
    this.boxPrice = this.querySelector('[data-box-price]');

    this.btn__add_body1 = this.querySelector('.btn__add_body1')
    this.btn__add_body2 = this.querySelector('.btn__add_body2')
    this.area_price = this.querySelector('[area-price]')
    this.feedbackEl = this.querySelector('[data-calc-feedback]')

    
    this.wallIndex1 = 1;
    this.wallIndex2 = 1;

    // 👇 the key: store a base sqm that never includes the +10%
    this.baseSqm = parseFloat(this.sqmInput?.value) || 0;

    this.setAccessibility();

    this.area__category_1 = this.querySelector(".area__category_1 .calculate-area-inner");
    this.area__category_2 = this.querySelector(".area__category_2 .calculate-area-inner");

    // The "Calculate Area" popup is m²-only; it isn't rendered in linear mode.
    if (this.area__category_1) this.Category1setAccessibility();
    if (this.area__category_2) this.Category2setAccessibility();
  }

  // Multi-variant products (e.g. border colours): when the variant changes, refresh the
  // per-tile price in place and recompute totals — the customer's entered quantity is kept.
  // Per-tile size is product-level (shared across variants), so only price needs updating.
  // The theme dispatches `variant:changed` on the product form (see custom.js / theme.js).
  bindVariantChange() {
    const form = this.closest('form');
    if (!form) return;
    form.addEventListener('variant:changed', (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant || variant.price == null) return;
      // theme variant prices are in minor units (pence); perTilePrice is in major units
      const newPrice = typeof variant.price === 'number' ? variant.price / 100 : parseFloat(variant.price);
      if (!isNaN(newPrice)) {
        this.perTilePrice = newPrice;
        this.refreshUnitPrice();
        // Only recompute when a quantity exists — otherwise blank inputs would be
        // filled with 0/0.00 on variant change (mirrors the blur guard above).
        if (this.baseSqm > 0) this.settiles();
      }
    });
  }

  // Move the theme's real Add to cart button up into the calculator's total row so the
  // price and the button share one line (per the redesign). We relocate the existing node
  // rather than recreate it, which keeps all theme behaviour (sold-out/disabled states,
  // loading spinner, payment button) and avoids a duplicate submit button in the form.
  relocateBuyButtons() {
    const mount = this.querySelector('[data-calc-buy-mount]');
    if (!mount || mount.children.length) return; // already relocated (e.g. on re-render)
    const form = this.closest('form');
    if (!form) return;
    const buyButtons = form.querySelector('.product-form__buy-buttons');
    if (!buyButtons || mount.contains(buyButtons)) return;
    mount.appendChild(buyButtons);
  }

  debounce(fn, delay = 800) {
    let timer;
    const debounced = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  }

  // Recalculation is debounced while typing; blur cancels any pending run and
  // commits immediately, so leaving the field never races a stale timer.
  bindQuantityInput(input, applyFn) {
    const debounced = this.debounce(() => applyFn.call(this));
    input.addEventListener('input', debounced);
    input.addEventListener('blur', () => {
      debounced.cancel();
      if (input.value.trim() === '') {
        // Field abandoned empty: restore the last valid values, don't treat as zero
        if (this.baseSqm > 0) this.settiles(null, null);
        return;
      }
      applyFn.call(this);
    });
  }

  // A blank field means the customer is mid-edit — wait for blur, never recalc on it
  applySqmEntry() {
    if (this.sqmInput.value.trim() === '') return;
    const entered = parseFloat(this.sqmInput.value);
    if (!entered || entered <= 0) {
      this.baseSqm = Math.max(this.baseSqm, this.perBoxQty * this.perTileUnit);
      this.settiles('min', null);
      return;
    }
    this.baseSqm = entered;
    this.settiles('sqm', entered);
  }

  applyTilesEntry() {
    if (this.tilesInput.value.trim() === '') return;
    const entered = parseFloat(this.tilesInput.value);
    if (!entered || entered <= 0) {
      this.baseSqm = Math.max(this.baseSqm, this.perBoxQty * this.perTileUnit);
      this.settiles('min', null);
      return;
    }
    // Keep baseSqm exact — rounding to 2dp here can inflate the area by up to
    // 0.005, which divides back to one tile more than entered and (on box-only
    // products) snaps a whole extra box. Display rounding happens in titalsqm().
    this.baseSqm = entered * this.perTileUnit;
    this.settiles('tiles', entered);
  }

  applyBoxEntry() {
    if (this.boxInput.value.trim() === '') return;
    const boxes = parseFloat(this.boxInput.value);
    if (!boxes || boxes < 1) {
      this.baseSqm = this.perBoxQty * this.perTileUnit;
      this.settiles('min-boxes', null);
      return;
    }
    // Exact, not round2 — see applyTilesEntry
    this.baseSqm = boxes * this.perBoxQty * this.perTileUnit;
    this.settiles('boxes', boxes);
  }

  setAccessibility() {
    this.bindQuantityInput(this.sqmInput, this.applySqmEntry);
    this.bindQuantityInput(this.tilesInput, this.applyTilesEntry);
    if (this.boxInput) {
      this.bindQuantityInput(this.boxInput, this.applyBoxEntry);
    }

    this.bindVariantChange();
    this.relocateBuyButtons();

    this.checkbox.addEventListener('change', () => {
      this.settiles('wastage');
    });

    if (this.btn__add_body1) {
      this.btn__add_body1.addEventListener('click', () => {
        this.addWallCategory1()
      });
    }

    if (this.btn__add_body2) {
      this.btn__add_body2.addEventListener('click', () => {
        this.addWallCategory2()
      });
    }


    // Selectors scoped to this component (area popup is m²-only — absent in linear mode)
    const openBtn = this.querySelector('.cal___icon_wrapper');
    const closeBtn = this.querySelector('.area__close-icon');
    const overlay = this.querySelector('.calculate-area-popup-overlay');
    const popup = this.querySelector('.calculate-area-popup-row');

    const popup_add_to_cart = popup ? popup.querySelector('.calculate-button') : null;

    if (popup_add_to_cart) {
      popup_add_to_cart.addEventListener('click', () => {
        document.querySelector('html').classList.remove('hidden');
        popup.classList.remove('active');
        document.querySelector('.product-block-list__wrapper .product-form__add-button').click()
      });
    }

    if (openBtn && popup) {
      openBtn.addEventListener('click', () => {
        document.querySelector('html').classList.add('hidden');
        popup.classList.add('active');
        this.updateTotalCategory();
      });
    }

    if (closeBtn && popup) {
      closeBtn.addEventListener('click', () => {
        document.querySelector('html').classList.remove('hidden');
        popup.classList.remove('active');
      });
    }

    if (overlay && popup) {
      overlay.addEventListener('click', () => {
        document.querySelector('html').classList.remove('hidden');
        popup.classList.remove('active');
      });
    }

    // Toggle parent .open on subtract___wrapper click (scoped to this component)
    this.querySelectorAll(".subtract___wrapper").forEach((element) => {
      element.addEventListener("click", function() {
        this.parentElement.classList.toggle("open");
      });
    });
  }

  Category1setAccessibility(){
    const remove = this.area__category_1.querySelectorAll('.remove__row');

    remove.forEach((elements, idx, array) => {
      if (elements.getAttribute('listener') !== 'true') {
        elements.addEventListener('click', (event) => {
            this.removeWallCategory1(event);
            this.updateTotalCategory();
        });
        elements.setAttribute('listener', 'true');
      }
    });

    const area__category_1 = this.area__category_1.querySelectorAll('.calculate-area--row');

    area__category_1.forEach((elements, idx, array) => {
      var length = elements.querySelector('[name="length"]');

      if (length.getAttribute('listener') !== 'true') {
        length.addEventListener('change', (event) => {
            this.updateAreaCategory1(event);
        });
        length.setAttribute('listener', 'true');
      }

      var width = elements.querySelector('[name="width"]');

      if (width.getAttribute('listener') !== 'true') {
        width.addEventListener('change', (event) => {
            this.updateAreaCategory1(event);
        });
        width.setAttribute('listener', 'true');
      }
    });
  }

  updateAreaCategory1(event){
    var input = event.target
    const row = input.closest(".calculate-area--row");
    const inputs = row.querySelectorAll("input");
    const length = parseFloat(inputs[0].value) || 0;
    const width = parseFloat(inputs[1].value) || 0;
    const area = length * width;
    row.querySelector(".row__info-total span").innerText = area.toFixed(2);

    this.updateTotalCategory();
  }

  removeWallCategory1(event){
    event.target.closest('.calculate-area--row')?.remove();
    this.wallIndex1--;
  }

  addWallCategory1(){
    this.wallIndex1++;
    const container = this.area__category_1;
    const row = document.createElement("div");
    row.className = "calculate-area--row";
    row.innerHTML = `
      <span class="row__title">Wall/Floor ${this.wallIndex1}</span>
      <div class="area--row__info">
          <span class="area--row--input">
            <label class="area--row--input-title">Length(m)</label>
            <input type="number" name="length" placeholder="0">
          </span>
          <span class="multiply__icon icon_area">
              <svg xmlns="http://www.w3.org/2000/svg" class="inline-block fill-current w-5 h-5 !w-[13px]" viewBox="0 0 24 24"><path d="M18.295 5.705a.998.998 0 0 1 0 1.41L13.41 12l4.885 4.885a.997.997 0 1 1-1.41 1.41L12 13.41l-4.885 4.885a.997.997 0 1 1-1.41-1.41L10.59 12 5.705 7.115a.997.997 0 0 1 1.41-1.41L12 10.59l4.885-4.885a.997.997 0 0 1 1.41 0Z"></path></svg>
          </span>
          <span class="area--row--input">
            <label class="area--row--input-title">Width(m)</label>
            <input type="number" name="width" placeholder="0">
          </span>
          <span class="equal__icon icon_area">=</span>
          <div class="row__info-total">
              <label class="area--row--input-title">Area(m²)</label>
              <span>0.00</span>
          </div>
          <span class="remove__row">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M2 12C2 6.47 6.47 2 12 2C17.53 2 22 6.47 22 12C22 17.53 17.53 22 12 22C6.47 22 2 17.53 2 12ZM16.295 16.295C16.6844 15.9056 16.6844 15.2744 16.295 14.885L13.41 12L16.295 9.115C16.6844 8.72564 16.6844 8.09436 16.295 7.705C15.9056 7.31564 15.2744 7.31564 14.885 7.705L12 10.59L9.115 7.705C8.72564 7.31564 8.09436 7.31564 7.705 7.705C7.31564 8.09436 7.31564 8.72564 7.705 9.115L10.59 12L7.705 14.885C7.31564 15.2744 7.31564 15.9056 7.705 16.295C8.09436 16.6844 8.72564 16.6844 9.115 16.295L12 13.41L14.885 16.295C15.2744 16.6844 15.9056 16.6844 16.295 16.295Z" class="fill-pdp-purchaseCard-calculatorPopup-body-crossIcon"></path>
            </svg>
          </span>
      </div>
    `;
    container.appendChild(row);
    this.Category1setAccessibility();
  }

  Category2setAccessibility(){
    const remove = this.area__category_2.querySelectorAll('.remove__row');

    remove.forEach((elements, idx, array) => {
      if (elements.getAttribute('listener') !== 'true') {
        elements.addEventListener('click', (event) => {
            this.removeWallCategory2(event);
            this.updateTotalCategory();
        });
        elements.setAttribute('listener', 'true');
      }
    });

    const area__category_2 = this.area__category_2.querySelectorAll('.calculate-area--row');

    area__category_2.forEach((elements, idx, array) => {
      var length = elements.querySelector('[name="length"]');

      if (length.getAttribute('listener') !== 'true') {
        length.addEventListener('change', (event) => {
            this.updateAreaCategory2(event);
        });
        length.setAttribute('listener', 'true');
      }

      var width = elements.querySelector('[name="width"]');

      if (width.getAttribute('listener') !== 'true') {
        width.addEventListener('change', (event) => {
            this.updateAreaCategory2(event);
        });
        width.setAttribute('listener', 'true');
      }
    });
  }

  updateAreaCategory2(event){
    var input = event.target
    const row = input.closest(".calculate-area--row");
    const inputs = row.querySelectorAll("input");
    const length = parseFloat(inputs[0].value) || 0;
    const width = parseFloat(inputs[1].value) || 0;
    const area = length * width;
    row.querySelector(".row__info-total span").innerText = area.toFixed(2);

    this.updateTotalCategory();
  }

  updateTotalCategory(){
    let total_1 = 0;
    this.area__category_1.querySelectorAll('.calculate-area--row .row__info-total span').forEach(span => {
      total_1 += parseFloat(span.innerHTML) || 0;
    });
    this.area__category_1.parentElement.querySelector(".calculate-area-total").innerText = "Total = " + total_1.toFixed(2);
    
    let total_2 = 0;
    this.area__category_2.querySelectorAll('.calculate-area--row .row__info-total span').forEach(span => {
      total_2 += parseFloat(span.innerHTML) || 0;
    });

    var final_total = total_1 - total_2;

    if(final_total <= 0){
      final_total = 0;
    }

    this.area__category_2.parentElement.querySelector(".calculate-area-total").innerText = "Total = " + final_total.toFixed(2);

    const totalsqm = final_total;
    this.baseSqm = this.round2(totalsqm);
    this.sqmInput.value = this.baseSqm;
    this.settiles('sqm', this.baseSqm);
  }

  removeWallCategory2(event){
    event.target.closest('.calculate-area--row')?.remove();
    this.wallIndex2--;
  }

  addWallCategory2(){
    this.wallIndex2++;
    const container = this.area__category_2;
    const row = document.createElement("div");
    row.className = "calculate-area--row";
    row.innerHTML = `
      <span class="row__title">Window/Door ${this.wallIndex2}</span>
      <div class="area--row__info">
          <span class="area--row--input">
            <label class="area--row--input-title">Length(m)</label>
            <input type="number" name="length" placeholder="0">
          </span>
          <span class="multiply__icon icon_area">
              <svg xmlns="http://www.w3.org/2000/svg" class="inline-block fill-current w-5 h-5 !w-[13px]" viewBox="0 0 24 24"><path d="M18.295 5.705a.998.998 0 0 1 0 1.41L13.41 12l4.885 4.885a.997.997 0 1 1-1.41 1.41L12 13.41l-4.885 4.885a.997.997 0 1 1-1.41-1.41L10.59 12 5.705 7.115a.997.997 0 0 1 1.41-1.41L12 10.59l4.885-4.885a.997.997 0 0 1 1.41 0Z"></path></svg>
          </span>
          <span class="area--row--input">
            <label class="area--row--input-title">Width(m)</label>
            <input type="number" name="width" placeholder="0">
          </span>
          <span class="equal__icon icon_area">=</span>
          <div class="row__info-total">
              <label class="area--row--input-title">Area(m²)</label>
              <span>0.00</span>
          </div>
          <span class="remove__row">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M2 12C2 6.47 6.47 2 12 2C17.53 2 22 6.47 22 12C22 17.53 17.53 22 12 22C6.47 22 2 17.53 2 12ZM16.295 16.295C16.6844 15.9056 16.6844 15.2744 16.295 14.885L13.41 12L16.295 9.115C16.6844 8.72564 16.6844 8.09436 16.295 7.705C15.9056 7.31564 15.2744 7.31564 14.885 7.705L12 10.59L9.115 7.705C8.72564 7.31564 8.09436 7.31564 7.705 7.705C7.31564 8.09436 7.31564 8.72564 7.705 9.115L10.59 12L7.705 14.885C7.31564 15.2744 7.31564 15.9056 7.705 16.295C8.09436 16.6844 8.72564 16.6844 9.115 16.295L12 13.41L14.885 16.295C15.2744 16.6844 15.9056 16.6844 16.295 16.295Z" class="fill-pdp-purchaseCard-calculatorPopup-body-crossIcon"></path>
            </svg>
          </span>
      </div>
    `;
    container.appendChild(row);
    this.Category2setAccessibility();
  }

  setsqm() {
    // kept for compatibility: tiles -> sqm, then recalc (exact, not round2 — see applyTilesEntry)
    const totalsqm = (parseFloat(this.tilesInput.value) || 0) * this.perTileUnit;
    this.baseSqm = totalsqm;
    this.sqmInput.value = this.baseSqm;
    this.settiles();
  }

  settiles(trigger = null, enteredValue = null) {
    this.totalPriceRow.classList.add('hide');

    const totalTiles = this.totalTiles();
    const totalBoxes = this.totalBoxes(totalTiles);
    const totalsqm = this.titalsqm(totalTiles);

    // Never rewrite the field the customer is editing — it gets normalised on blur
    const active = document.activeElement;
    if (active !== this.sqmInput) this.sqmInput.value = totalsqm;
    if (active !== this.tilesInput) this.tilesInput.value = totalTiles;
    if (this.boxInput && active !== this.boxInput) this.boxInput.value = totalBoxes;

    this.setPrice(totalsqm, totalTiles);
    this.updateFeedback(trigger, enteredValue, totalTiles);
  }

  // The "/SQM" or "/Linear M" header price is rendered once in Liquid from the first variant.
  // When the variant changes we recompute it from the new per-tile price so it never goes stale.
  refreshUnitPrice() {
    if (!this.unitPrice || !this.perTileUnit) return;
    const perUnit = this.perTilePrice / this.perTileUnit;
    this.unitPrice.textContent = "£" + this.round2(perUnit).toFixed(2);
    // Header secondary price (per box / per tile) — display-only, keep in sync on variant change.
    if (this.boxPrice) {
      const secondary = this.boxOnly ? this.perTilePrice * this.perBoxQty : this.perTilePrice;
      this.boxPrice.textContent = "£" + this.round2(secondary).toFixed(2);
    }
  }

  setPrice(_totalsqm, totalTiles) {
    const totalPrice = totalTiles * this.perTilePrice;
    this.totalPrice.textContent = "£" + this.round2(totalPrice).toFixed(2);
    if (this.area_price) this.area_price.textContent = "£" + this.round2(totalPrice).toFixed(2);
    this.closest('form').querySelector('[name="quantity"]').value = totalTiles;
    this.totalPriceRow.classList.remove('hide');
  }

  titalsqm(totalTiles) {
    return this.round2(totalTiles * this.perTileUnit).toFixed(2);
  }

  totalTiles() {
    const rawTiles = Math.ceil(Math.round((this.baseSqm / this.perTileUnit || 0) * 1e10) / 1e10);

    if (!this.boxOnly) {
      if (this.checkbox.checked) {
        return Math.ceil(rawTiles * this.wastage);
      }
      return rawTiles;
    }

    const boxes = Math.ceil(rawTiles / this.perBoxQty);
    const baseBoxCount = boxes * this.perBoxQty;
    if (this.checkbox.checked) {
      return baseBoxCount + this.perBoxQty;
    }
    return baseBoxCount;
  }

  totalBoxes(totalTiles) {
    return Math.ceil(totalTiles / this.perBoxQty);
  }

  round2(n) {
    return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  }

  updateFeedback(trigger, entered, totalTiles) {
    if (!this.feedbackEl) return;

    const boxes = this.totalBoxes(totalTiles);
    const boxWord = boxes === 1 ? 'box' : 'boxes';

    if (trigger === 'min' || trigger === 'min-boxes') {
      const resetTo = trigger === 'min-boxes'
        ? `${boxes} ${boxWord}`
        : `${totalTiles} tiles`;
      this.setFeedback('min', `Minimum order is 1 box. Reset to ${resetTo}.`);
      return;
    }

    if (trigger === 'boxes') {
      this.setFeedback('exact', `Now ${boxes} ${boxWord}.`);
      return;
    }

    if (trigger === 'tiles') {
      if (this.boxOnly) {
        if (entered === totalTiles) {
          this.setFeedback('exact', `Exactly ${boxes} full ${boxWord}.`);
        } else {
          this.setFeedback('snap',
            `${entered} tiles rounded up to ${totalTiles} tiles (${boxes} ${boxWord}), the nearest full box.`);
        }
      } else {
        this.setFeedback('exact', `${totalTiles} tiles calculated.`);
      }
      return;
    }

    if (trigger === 'sqm') {
      const enteredFmt = parseFloat(entered).toFixed(2);
      const resultSqm = this.titalsqm(totalTiles);
      if (this.boxOnly) {
        if (enteredFmt === resultSqm) {
          this.setFeedback('exact', `Exactly ${boxes} full ${boxWord}.`);
        } else {
          this.setFeedback('snap',
            `${enteredFmt} ${this.unitLabel} rounded up to ${resultSqm} ${this.unitLabel} (${boxes} ${boxWord}), the nearest full box.`);
        }
      } else {
        if (enteredFmt === resultSqm) {
          this.setFeedback('exact', `${totalTiles} tiles calculated.`);
        } else {
          this.setFeedback('snap', `${enteredFmt} ${this.unitLabel} rounded up to ${totalTiles} tiles.`);
        }
      }
      return;
    }

    if (trigger === 'wastage') {
      if (this.checkbox.checked) {
        if (this.boxOnly) {
          this.setFeedback('snap', '1 extra box added for cuts and waste.');
        } else {
          const preWastageTiles = Math.ceil(this.baseSqm / this.perTileUnit || 0);
          this.setFeedback('snap', `Wastage added: ${preWastageTiles} → ${totalTiles} tiles.`);
        }
      } else {
        this.feedbackEl.className = 'calc-feedback';
        this.feedbackEl.querySelector('[data-feedback-icon]').innerHTML = '';
        this.feedbackEl.querySelector('[data-feedback-text]').textContent = '';
      }
      return;
    }

    // No trigger — clear the line
    this.feedbackEl.className = 'calc-feedback';
    this.feedbackEl.querySelector('[data-feedback-icon]').innerHTML = '';
    this.feedbackEl.querySelector('[data-feedback-text]').textContent = '';
  }

  setFeedback(type, text) {
    if (!this.feedbackEl) return;
    this.feedbackEl.className = 'calc-feedback calc-feedback--' + type;
    this.feedbackEl.querySelector('[data-feedback-icon]').innerHTML = {
      snap:  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>',
      exact: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
      min:   '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
    }[type] || '';
    this.feedbackEl.querySelector('[data-feedback-text]').textContent = text;
  }
}

customElements.define('tiles-calculator', TilesCalculator);